import { z } from 'zod'
import type { Policy, ScopedInstance } from './types'

const instanceId = z.preprocess(
  (value) =>
    typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : value,
  z.number().int().positive().max(Number.MAX_SAFE_INTEGER)
)

export const opportunitySearchSchema = z.object({
  current_only: z.boolean().optional().catch(undefined),
  category: z
    .enum(['cost', 'capacity', 'reliability', 'security'])
    .optional()
    .catch(undefined),
  eligibility: z
    .enum(['eligible', 'needs_evidence', 'blocked', 'no_longer_applicable'])
    .optional()
    .catch(undefined),
  state: z
    .enum([
      'open',
      'in_review',
      'dismissed',
      'deferred',
      'planned',
      'executing',
      'verifying',
      'completed',
      'failed',
    ])
    .optional()
    .catch(undefined),
  instance_id: instanceId.optional().catch(undefined),
  offset: z.coerce
    .number()
    .int()
    .nonnegative()
    .max(Number.MAX_SAFE_INTEGER)
    .optional()
    .catch(undefined),
})
export type OpportunityReturnSearch = z.infer<typeof opportunitySearchSchema>

export const opportunityDetailSearchSchema = z.object({
  return_search: opportunitySearchSchema.optional().catch(undefined),
})
export const optimizationSettingsSearchSchema = z.object({
  instance: instanceId.optional().catch(undefined),
})

// This chooses the most specific active editing scope, not a new policy or an
// attestation. The API remains authoritative for access and merged objectives.
export function applicablePolicy(
  policies: Policy[],
  instance: ScopedInstance
): Policy | undefined {
  const active = policies.filter((policy) => policy.is_active)
  return (
    active.find((policy) => policy.instance_id === instance.id) ??
    active.find(
      (policy) =>
        !policy.instance_id &&
        instance.site_id !== null &&
        policy.site_id === instance.site_id
    ) ??
    active.find(
      (policy) =>
        policy.scope_key === 'organization' &&
        !policy.site_id &&
        !policy.instance_id
    )
  )
}

export function setupDraftKey(
  organizationId: number | null | undefined,
  actorId: number | undefined,
  policyId?: number,
  contextualInstanceId?: number
) {
  const subject =
    policyId ??
    (contextualInstanceId ? `new:instance:${contextualInstanceId}` : 'new')
  return `smartdba.optimization.draft:v1:${organizationId}:${actorId}:${subject}`
}
